import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { DirectUpload } from "@rails/activestorage"
import { Sortable } from "sortablejs"
import { extractGeodata } from "../helpers/image_geolocation"

export default class extends Controller {
  static targets = [
    "input",
    "previews",
    "previewTemplate",
    "filename",
    "nameField",
    "editContainer",
    "editButton",
    "cameraInput",
    "clickable",
  ]

  // Only forms that opt in (data-uploads-geolocate-value="true") collect GPS
  // coordinates for uploaded photos. This keeps the location permission prompt
  // off the many other upload forms that share this controller.
  //
  // Forms whose files are the ordered pages of one document (an expense receipt) opt into
  // data-uploads-sortable-value="true": the previews can then be dragged into page order.
  static values = {
    geolocate: { type: Boolean, default: false },
    sortable: { type: Boolean, default: false },
  }

  connect() {
    Dropzone.autoDiscover = false
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
      this.inputTarget.style.display = "none"
    }
    this.uploadedFiles = new Map()
    // Keyed by preview element rather than file name: phone cameras name every photo
    // "image.jpg", and the pages of one receipt are exactly such a series.
    this.uploadsByPreview = new Map()

    let multiple = this.hasInputTarget ? this.inputTarget.getAttribute("multiple") : null
    const is_settings_page = this.element.action.includes("settings")
    multiple = is_settings_page ? false : multiple
    const is_material_item_documents_page = this.element.action.includes("material_item_documents")

    let options = {
      url: "/",
      maxFiles: multiple ? "100" : "1",
      maxFilesize: is_settings_page ? "5" : "250",
      autoQueue: false,
      previewTemplate: this.previewTemplateTarget.innerHTML,
      previewsContainer: this.previewsTarget,
      // Dropzone's default click target is the whole controller element, which
      // is the form. That is fine when the submit button lives outside the form
      // (the offcanvas uploaders), but a form with its own button inside would
      // open the file picker when that button is clicked. Marking a narrower
      // element as the clickable target avoids it; without the target nothing
      // changes for the existing forms.
      clickable: this.hasClickableTarget ? this.clickableTarget : true,
    }
    if (is_settings_page) {
      options = Object.assign(options, { acceptedFiles: ".jpeg,.jpg,.png,.gif" })
    }
    const dropzone = new Dropzone(this.element, options)
    this.dropzone = dropzone
    if (this.sortableValue && multiple) {
      this.sortable = Sortable.create(this.previewsTarget, {
        animation: 150,
        onEnd: () => {
          this.orderBlobInputs()
          this.dispatch("filesReordered", { detail: this.filesDetail(), bubbles: true })
        },
      })
    }
    this.submitButton = document.querySelector(this.data.get("submitButtonTarget"))
    dropzone.on("addedfile", (file) => {
      this.handleFileAdd(file, dropzone, is_material_item_documents_page)
    })

    dropzone.on("success", (file) => {
      this.handleFileSuccess(file, is_material_item_documents_page)
    })

    dropzone.on("removedfile", (file) => {
      this.handleFileRemove(file)
    })
  }

  handleFileAdd(file, dropzone, is_material_item_documents_page) {
    if (this.submitButton) {
      this.submitButton.disabled = true
      this.submitButton.classList.add("disabled-button")
    }
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.disabled = true
      this.editButtonTarget.classList.add("disabled-button")
    }

    setTimeout(() => {
      // Removed again before its upload began.
      if (!dropzone.files.includes(file)) return
      if (file.accepted && (!is_material_item_documents_page || file.type === "application/pdf")) {
        // Kicked off alongside the upload rather than after it: reading EXIF and (for photos
        // without it) acquiring a GPS fix can take seconds, and the upload is dead time we can
        // spend on it. Never awaited on the path that re-enables the submit button.
        const geodataPromise = this.collectGeodata(file)

        const upload = new DirectUpload(file, this.url)
        upload.create((error, blob) => {
          if (error) {
            console.error("Upload error:", error)
            // No longer pending: listeners waiting for the whole set act on the other files.
            file.uploadFailed = true
            file.previewElement.querySelector(".uploading")?.classList.add("hidden")
            file.previewElement.querySelector(".forbidden")?.classList.remove("hidden")
            this.dispatch("uploadFailed", { detail: this.filesDetail(), bubbles: true })
            return
          }
          // Removed while it was uploading: its blob must not be submitted after all.
          if (!dropzone.files.includes(file)) return

          // Create hidden input for blob ID
          const blobInput = document.createElement("input")
          blobInput.type = "hidden"
          blobInput.name = this.hasInputTarget ? this.inputTarget.name : "file"
          blobInput.value = blob.signed_id
          const parentNode = this.hasInputTarget ? this.inputTarget.parentNode : this.element
          parentNode.insertBefore(
            blobInput,
            this.hasInputTarget ? this.inputTarget.nextSibling : parentNode.firstChild,
          )

          // Create hidden input for filename with blob ID reference
          const filenameInput = document.createElement("input")
          filenameInput.type = "hidden"
          filenameInput.name = "[new_file_names][]"
          filenameInput.value = file.name
          filenameInput.dataset.blobId = blob.signed_id // Store the blob ID reference
          parentNode.insertBefore(
            filenameInput,
            this.hasInputTarget ? this.inputTarget.nextSibling : parentNode.firstChild,
          )

          // Store file information. geodataInputs is filled in later, in place, so that removing
          // the file still cleans up inputs that had not arrived yet when it was registered.
          const fileInfo = {
            element: file.previewElement,
            blob: blob,
            filenameInput: filenameInput,
            blobInput: blobInput,
            geodataInputs: [],
          }
          this.uploadedFiles.set(file.name, fileInfo)
          this.uploadsByPreview.set(file.previewElement, fileInfo)
          this.orderBlobInputs()

          // GPS coordinates + capture timestamp, keyed by the blob's signed id so the server can
          // persist them as blob metadata. Deliberately not awaited: the submit button is
          // re-enabled by the "success" event below, and a location prompt the user never
          // answers must not be able to hold the form hostage. Worst case the geodata loses the
          // race against a fast submit and the photo is stored without coordinates.
          void geodataPromise.then((geodata) => {
            fileInfo.geodataInputs = this.appendGeodataInputs(geodata, blob, parentNode)
          })

          // Notify other Stimulus controllers on the form (e.g. expense receipt
          // extraction) that an Active Storage upload finished. The Stimulus
          // convention namespaces the event as `uploads:fileUploaded`.
          this.dispatch("fileUploaded", {
            detail: {
              signedId: blob.signed_id,
              filename: file.name,
              contentType: file.type,
              ...this.filesDetail(),
            },
            bubbles: true,
          })

          dropzone.emit("success", file)
          dropzone.emit("complete", file)
        })
      } else {
        file.previewElement.querySelector(".uploading").classList.add("hidden")
        file.previewElement.querySelector(".forbidden").classList.remove("hidden")
      }
    }, 500)
  }

  handleFileSuccess(file, is_material_item_documents_page) {
    const fileInfo = this.uploadedFiles.get(file.name)
    if (!fileInfo) return

    const container = fileInfo.element
    container.querySelector(".uploading").classList.add("hidden")
    container.querySelector(".completed").classList.remove("hidden")

    // Add data attributes to help track this file
    container.dataset.originalName = file.name
    container.dataset.blobId = fileInfo.blob.signed_id

    if (!is_material_item_documents_page || file.type === "application/pdf") {
      if (document.querySelectorAll(".completed.hidden").length === 1) {
        if (this.submitButton) {
          this.submitButton.disabled = false
          this.submitButton.classList.remove("disabled-button")
        }
        if (this.hasEditButtonTarget) {
          this.editButtonTarget.disabled = false
          this.editButtonTarget.classList.remove("disabled-button")
        }
      }
    }
  }

  // Resolves to the file's geodata, or null when the form has not opted in, the file is not an
  // image, or extraction failed. Always resolves — never rejects — because the upload flow reads
  // it with .then() and a rejection there would strand the file mid-upload.
  async collectGeodata(file) {
    if (!this.geolocateValue) return null
    if (!file.type || !file.type.startsWith("image/")) return null

    try {
      return await extractGeodata(file)
    } catch (error) {
      console.warn("Photo geodata extraction failed:", error)
      return null
    }
  }

  appendGeodataInputs(geodata, blob, parentNode) {
    if (!geodata) return []

    const inputs = []

    Object.entries(geodata).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return

      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `file_geodata[${blob.signed_id}][${key}]`
      input.value = value
      parentNode.insertBefore(input, parentNode.firstChild)
      inputs.push(input)
    })

    return inputs
  }

  handleFileRemove(file) {
    // By preview only: a lookup by name would find another "image.jpg" when this one was
    // removed before its upload finished, and drop that page's inputs instead.
    const fileInfo = this.uploadsByPreview.get(file.previewElement)
    if (fileInfo) {
      fileInfo.filenameInput.remove()
      fileInfo.blobInput.remove()
      fileInfo.geodataInputs?.forEach((input) => input.remove())
      if (this.uploadedFiles.get(file.name) === fileInfo) this.uploadedFiles.delete(file.name)
      this.uploadsByPreview.delete(file.previewElement)
    }
    // Also for a file removed mid-upload: it no longer counts as pending.
    this.dispatch("fileRemoved", { detail: this.filesDetail(), bubbles: true })
  }

  disconnect() {
    this.sortable?.destroy()
  }

  // The uploaded files' signed ids in the order their previews stand, and how many accepted
  // files are still uploading — listeners wait for those before acting on the whole set.
  filesDetail() {
    const signedIds = Array.from(this.previewsTarget.children)
      .map((element) => this.uploadsByPreview.get(element)?.blob.signed_id)
      .filter(Boolean)
    const pending = (this.dropzone?.files ?? []).filter(
      (file) =>
        file.accepted && !file.uploadFailed && !this.uploadsByPreview.has(file.previewElement),
    ).length
    return { signedIds, pending }
  }

  // Uploads finish in any order; the submitted ids follow the previews, so the server gets
  // the pages of a multi-file input in the order the user sees (and dragged) them.
  orderBlobInputs() {
    if (!this.hasInputTarget || !this.inputTarget.multiple) return

    let anchor = this.inputTarget
    Array.from(this.previewsTarget.children).forEach((element) => {
      const fileInfo = this.uploadsByPreview.get(element)
      if (!fileInfo) return
      anchor.after(fileInfo.blobInput)
      anchor = fileInfo.blobInput
    })
  }

  editFileName(event) {
    const container = event.target.closest(".mt-1")
    const originalName = container.dataset.originalName
    const fileInfo = this.uploadedFiles.get(originalName)
    if (!fileInfo) return

    // Create text input for editing
    const editInput = document.createElement("input")
    editInput.type = "text"
    editInput.className = "mt-2 block w-full rounded-md border-gray-300"
    editInput.value = fileInfo.filenameInput.value.replace(/\.[^/.]+$/, "") // Remove extension for editing
    editInput.dataset.originalName = originalName

    const editContainer = container.querySelector(".edit-container")
    editContainer.insertBefore(editInput, editContainer.firstChild)

    // Toggle buttons
    editContainer.querySelector(".edit-button").classList.add("hidden")
    editContainer.querySelector(".save-button").classList.remove("hidden")
    editContainer.querySelector(".cancel-button").classList.remove("hidden")

    editInput.focus()
  }

  saveFileName(event) {
    const container = event.target.closest(".mt-1")
    const editContainer = container.querySelector(".edit-container")
    const editInput = editContainer.querySelector('input[type="text"]')
    const originalName = editInput.dataset.originalName
    const fileInfo = this.uploadedFiles.get(originalName)

    if (!fileInfo) return

    const extension = this.getFileExtension(originalName)
    const newName = `${editInput.value.trim()}${extension}`

    // Update the filename input value and ensure blob ID reference is maintained
    fileInfo.filenameInput.value = newName

    container.querySelector("[data-dz-name]").textContent = newName

    // Clean up
    editInput.remove()
    editContainer.querySelector(".edit-button").classList.remove("hidden")
    editContainer.querySelector(".save-button").classList.add("hidden")
    editContainer.querySelector(".cancel-button").classList.add("hidden")
  }

  cancelEdit(event) {
    const container = event.target.closest(".mt-1")
    const editContainer = container.querySelector(".edit-container")
    const editInput = editContainer.querySelector('input[type="text"]')

    // Clean up
    editInput.remove()
    editContainer.querySelector(".edit-button").classList.remove("hidden")
    editContainer.querySelector(".save-button").classList.add("hidden")
    editContainer.querySelector(".cancel-button").classList.add("hidden")
  }

  handleCameraInput(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    const dropzone = Dropzone.forElement(this.element)
    for (const file of files) {
      dropzone.addFile(file)
    }

    // Reset input so the same file can be selected again
    event.target.value = ""
  }

  getFileExtension(filename) {
    return filename.substring(filename.lastIndexOf("."))
  }

  get url() {
    return this.hasInputTarget
      ? this.inputTarget.getAttribute("data-direct-upload-url")
      : this.element.getAttribute("data-direct-upload-url")
  }
}
